import { DownOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SubmittedPensionContributionTab from './SubmittedPensionContributionTab';
import { Icons } from '@nxsys/types/icons';
import { Dropdown } from 'antd';
import PendingPensionContributionTab from './PendingPensionContributionTab';

export enum PENSION_CONTRIBUTION_TAB {
  SUCCESS_SUBMISSION = 'SUCCESS_SUBMISSION',
  PENDING_SUBMISSION = 'PENDING_SUBMISSION',
  FAILED_SUBMISSION = 'FAILED_SUBMISSION',
}

export type PensionContributionTabRef = {
  exportSelected?: () => void;
  exportAll?: () => void;
  exportErrorsSelected?: () => void;
  exportAllErrors?: () => void;
  markAsSubmitted?: () => void;
  resubmit?: () => void;
  submitPensionFromPayroll?: () => void;
  approvePayment?: () => void;
};

type Props = {
  isDashboard?: boolean;
};

const PensionContributionReport: React.FC<Props> = ({
  isDashboard = false,
}) => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const { goBack } = useGoBackByQuery();
  const [searchParams] = useSearchParams();
  const tabParams = searchParams.get('tab') as PENSION_CONTRIBUTION_TAB;
  const pensionContributionReportRef = useRef<PensionContributionTabRef>(null);

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.pensionContributionReport'],
        key: PENSION_CONTRIBUTION_TAB.SUCCESS_SUBMISSION,
      },
      {
        tab: messages['common.pendingSubmission'],
        key: PENSION_CONTRIBUTION_TAB.PENDING_SUBMISSION,
      },
      {
        tab: messages['common.errors'],
        key: PENSION_CONTRIBUTION_TAB.FAILED_SUBMISSION,
      },
    ],
    [messages],
  );

  const [activeTab, setActiveTab] = useState<PENSION_CONTRIBUTION_TAB>(
    PENSION_CONTRIBUTION_TAB.SUCCESS_SUBMISSION,
  );

  useEffect(() => {
    if (tabParams) {
      setActiveTab(tabParams);
    }
  }, [tabParams]);

  const handleTabChange = useCallback(
    (key: PENSION_CONTRIBUTION_TAB) => {
      setActiveTab(key);
      if (isDashboard) return;
      navigate('/reports/pension/contribution-report', {
        query: {
          tab: key,
        },
      });
    },
    [isDashboard, navigate],
  );

  const handleGoBack = useCallback(
    () =>
      goBack({
        fromPathQueryKey: 'from',
        fallbackPathDefault: '/reports',
        excludeParams: ['tab'],
      }),
    [goBack],
  );

  const renderTitle = useCallback(() => {
    return (
      <div
        className={`flex items-center gap-x-4 ${isDashboard ? 'ml-[16px]' : ''}`}
      >
        {!isDashboard && <AppBackButton onClick={handleGoBack} />}
        <AppPageTitle
          prefix={messages['common.pensionContributionReport']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack, isDashboard]);

  const renderRightContent = () => {
    const menuProps = {
      items: [
        {
          key: 'export',
          label: messages['common.export'],
          onClick: () =>
            pensionContributionReportRef.current?.exportErrorsSelected?.(),
        },
        {
          key: 'exportAll',
          label: messages['common.exportAll'],
          onClick: () =>
            pensionContributionReportRef.current?.exportAllErrors?.(),
        },
        {
          key: 'markAsSubmitted',
          label: messages['common.markAsSubmitted'],
          onClick: () =>
            pensionContributionReportRef.current?.markAsSubmitted?.(),
        },
        {
          key: 'resubmit',
          label: messages['common.resubmit'],
          onClick: () => pensionContributionReportRef.current?.resubmit?.(),
        },
      ],
    };

    return (
      <div
        className={`flex items-center gap-x-4 ${isDashboard ? 'mr-[48px] mt-4' : ''}`}
      >
        {activeTab === PENSION_CONTRIBUTION_TAB.SUCCESS_SUBMISSION && (
          <>
            <AppButton
              name={messages['common.export']}
              variant="outlined"
              type="primary"
              leftIcon={<Icons.IconArrowExport />}
              onClick={() =>
                pensionContributionReportRef.current?.exportSelected?.()
              }
            />
            <AppButton
              type="primary"
              name={messages['common.exportAll']}
              leftIcon={<Icons.IconArrowExport color="#fff" />}
              onClick={() =>
                pensionContributionReportRef.current?.exportAll?.()
              }
            />
          </>
        )}
        {activeTab === PENSION_CONTRIBUTION_TAB.FAILED_SUBMISSION && (
          <Dropdown menu={menuProps}>
            <AppButton
              type="primary"
              name={messages['common.action']}
              rightIcon={<DownOutlined />}
            />
          </Dropdown>
        )}
        {activeTab === PENSION_CONTRIBUTION_TAB.PENDING_SUBMISSION && (
          <>
            <AppButton
              name={messages['common.approvePayment']}
              variant="outlined"
              onClick={() =>
                pensionContributionReportRef.current?.approvePayment?.()
              }
            />
            <AppButton
              name={messages['common.submit']}
              onClick={() =>
                pensionContributionReportRef.current?.submitPensionFromPayroll?.()
              }
            />
          </>
        )}
      </div>
    );
  };

  const renderTabContent = useCallback(() => {
    if (activeTab === PENSION_CONTRIBUTION_TAB.SUCCESS_SUBMISSION) {
      return (
        <SubmittedPensionContributionTab
          key={PENSION_CONTRIBUTION_TAB.SUCCESS_SUBMISSION}
          pensionContributionReportRef={pensionContributionReportRef}
          pensionTabType={PENSION_CONTRIBUTION_TAB.SUCCESS_SUBMISSION}
        />
      );
    }
    if (activeTab === PENSION_CONTRIBUTION_TAB.FAILED_SUBMISSION) {
      return (
        <SubmittedPensionContributionTab
          key={PENSION_CONTRIBUTION_TAB.FAILED_SUBMISSION}
          pensionContributionReportRef={pensionContributionReportRef}
          pensionTabType={PENSION_CONTRIBUTION_TAB.FAILED_SUBMISSION}
        />
      );
    }
    return (
      <PendingPensionContributionTab
        key={PENSION_CONTRIBUTION_TAB.PENDING_SUBMISSION}
        pensionContributionReportRef={pensionContributionReportRef}
      />
    );
  }, [activeTab]);

  const renderContent = () => (
    <AppCardTabListContainer
      tabList={tabs}
      activeTab={activeTab}
      handleTabChange={handleTabChange}
      {...(isDashboard && { className: '!border-none' })}
    >
      {renderTabContent()}
    </AppCardTabListContainer>
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      {isDashboard ? renderContent() : <AppCard>{renderContent()}</AppCard>}
    </AppsContainer>
  );
};

export default PensionContributionReport;
